import { useState, useCallback, useEffect, useMemo, forwardRef, memo, useImperativeHandle, useRef } from 'react';
import styles from '@stylesheet';
import apis from '@api/api';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import BottomSheet, {
  BottomSheetModal,
  BottomSheetBackgroundProps
} from '@gorhom/bottom-sheet';
import Animated, {
  useAnimatedStyle,
  interpolateColor,
} from "react-native-reanimated";
import React from 'react';
import parseByDelimiter from '@utils/parseByDelimiter';
import { Camera, CameraType } from 'expo-camera';
import { Image, TouchableOpacity, Pressable, Platform } from 'react-native';
import { View, Text, Title, BlurView } from '@constants/Themed';
import Button from '@components/UI/Button/Button';
import HandleBarCode from '@utils/handleScanData';
import { Ionicons, FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import dateHandler from '@hooks/dateHandler';
import GetAvatarImage from '@hooks/getAvatarImage';
import toTitleCase from '@hooks/toTitleCase';
import { isLandscape, isLandscapeSync, isTablet } from 'react-native-device-info';
import { useDeviceType } from '@hooks/useDeviceType';
import { useQueryClient } from '@tanstack/react-query';
import handleImage from '@utils/handleImage';
import * as Animatable from 'react-native-animatable';
interface AddDataModalProps {
  actions?: any;
  dataSchema: any;
  dataTitle?: string;
  dataId?: string;
  callback?: Function;
  createdAt?: string;
  owner?: Object;
  isProductCategory?: boolean;
}

export type AddDataModalHandle = {
  hideCamera: () => void;
  showCamera: () => void;
}

const AddDataModal = forwardRef<[BottomSheet, AddDataModalHandle], AddDataModalProps>((props, ref) => {
  // const [type, setType] = useState(CameraType?.back);
  const [camera, setCamera] = useState(false);
  // const [permission, requestPermission] = Camera.getCameraPermissionsAsync();
  const [sheet, setSheet] = useState()
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const Actions = memo(props.actions);
  const dataSchema = props.dataSchema;
  const title = props.dataTitle ? props.dataTitle : ''
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const tablet = isTablet;
  const landscape = isLandscapeSync();
  const drawerRef = useRef<BottomSheetModal>(null);
  const queryClient = useQueryClient();
  
  const snapPoints = useMemo(() => [tablet ? (landscape ? '20%' : '15%') : '20%', '90%'], []);

  useImperativeHandle(ref, () => {
    return {
      hideCamera: () => {
        setCamera(false);
      },
      showCamera: () => {
        setCamera(true);
        drawerRef.current?.snapToIndex(1);
      },
      showSheet: () => {
        setCamera(false);
        drawerRef.current?.snapToIndex(1);
      },
      hideSheet: () => {
        drawerRef.current?.snapToIndex(0);
      }
    }
  }, []);

  const obj = {};
  
  dataSchema?.map((item) => {
    const [[key, value]] = Object.entries(item); 
    obj[key] = value;
  });

  const handleCallback = useCallback((item) => {
    if(props.callback){
      props.callback(item);
    }
  }, []);

  const handleImageSelect = useCallback(async () => {
    try {
      const result = await handleImage(false); // Get base64
      if (result && typeof result === 'string') {
        // result is base64 string when local=false
        setImageBase64(result);
        // For preview, we need to convert base64 to data URI
        setSelectedImage(`data:image/jpeg;base64,${result}`);
      }
    } catch (error) {
      console.error('Error selecting image:', error);
    }
  }, []);

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImageBase64(null);
  }, []);

  // Wrapper function to include image in form submission
  const handleSubmitWithImage = useCallback(async (values: any, params: any) => {
    // Include the image if it was selected
    const dataToSubmit = imageBase64
      ? { ...values, 'Product Image': `data:image/jpeg;base64,${imageBase64}` }
      : values;

    return await apis.addDataItem(dataToSubmit, params || { dataId: props.dataId });
  }, [imageBase64, props.dataId]);


  // function toggleCameraType() {
  //   setType(current => (current === CameraType.back ? CameraType.front : CameraType.back));
  // }

  // callbacks
  const handleSheetChanges = useCallback((index: number) => {
    setSheet(index);
  }, [ref]);

  return (
    <BottomSheet
      ref={drawerRef}
      index={0}
      snapPoints={snapPoints}
      enableDynamicSizing={false}
      onChange={handleSheetChanges}
      handleIndicatorStyle={{ backgroundColor: theme ? 'black' : 'white', top: -5 }} 
      backgroundStyle={{ backgroundColor: theme ? 'white' : 'transparent' }} 
      style={[styles.bottomSheet, { backgroundColor: '#333333' }]}
    >
      {
        props.actions && sheet != 1 ?
          <Actions />
          :
          <View style={{ flex: 1, padding: 10, backgroundColor: 'transparent' }}>
        {
              camera ?
                <View style={{ flex: 1, paddingBottom: 150 }}>
                  <HandleBarCode type={undefined} saveCallback={async (val: Object) => {
                    await apis.addDataItem(val, { dataId: props.dataId }).then(() => {
                      queryClient.invalidateQueries({ queryKey: ['dataFolders'] });
                      handleCallback(val);
                    }).catch((e) => {
                      console.error('Error adding data item:', e);
                    });
                  }} data={dataSchema} />
                </View>
            :
                <View style={{ flex: 1, paddingBottom: "25%", backgroundColor: 'transparent' }}>
                  <View style={{ flexDirection: 'row', backgroundColor:'transparent' }}>
                    <Title numberOfLines={2} style={{ top: -8, flex: 3 }}>{parseByDelimiter(title)}</Title>
                    <Text numberOfLines={1} style={{ flex: 2, textAlign: 'right', lineHeight: 15, fontSize: 10 }}>{toTitleCase(props.owner?.profile_information?.name) + ' ' + toTitleCase(props.owner?.profile_information?.surname)} {'\n'} {dateHandler(props.createdAt, true) || ""}</Text>
                  </View>

                  {/* Product Image Selection - Above the form */}
                  {props.isProductCategory && (
                    <View style={{ marginVertical: 15, marginBottom: 20, maxHeight:"10%" }}>
                      <Pressable onPress={handleImageSelect} style={{
                        minHeight: "8%",
                        borderRadius: 12,
                        borderWidth: selectedImage ? 0 : 2,
                        borderStyle: 'dashed',
                        borderColor: theme ? '#ccc' : '#666',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme ? '#f5f5f5' : '#2a2a2a',
                        overflow: 'hidden'
                      }}>
                        {selectedImage ? (
                          <View style={{ width: '100%', height: '100%', position: 'relative' }}>
                            <Image
                              source={{ uri: selectedImage }}
                              style={{ width: '100%', height: 150, resizeMode: 'cover' }}
                            />
                            <Pressable
                              onPress={handleRemoveImage}
                              style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                borderRadius: 15,
                                padding: 6,
                                zIndex: 10
                              }}
                            >
                              <FontAwesome name="times" size={16} color="white" />
                            </Pressable>
                          </View>
                        ) : (
                          <View style={{ alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                            <MaterialIcons name="add-photo-alternate" size={40} color={theme ? '#999' : '#666'} />
                            <Text style={{ marginTop: 10, color: theme ? '#999' : '#666', fontSize: 14 }}>Tap to add product image</Text>
                          </View>
                        )}
                      </Pressable>
                    </View>
                  )}

                  <GenerateSchemaForm
                    customStyling={{ left: !isTablet ? 5 : 30, backgroundColor: 'transparent', maxHeight: 300 }}
                    buttonLabel='Submit'
                    scrollEnabled
                    buttonStyle={{ backgroundColor: 'rgb(90,192,234)' }}
                    schema={dataSchema}
                    extraFunction={handleCallback}
                    submitFunction={handleSubmitWithImage}
                    nullify={undefined}
                    extraCallBack={() => {
                      queryClient.invalidateQueries({ queryKey: ['dataFolders'] });
                      // Reset image after successful submission
                      setSelectedImage(null);
                      setImageBase64(null);
                    }}
                    sheetRef={ref}
                    other={props?.dataId}
                    buttonColor='rgb(90,192,234)'
                    // avoidKeyboardDisabled
                    addOffset={260}
                    hasTitle />
                </View>
        }
      </View>
      }
    </BottomSheet>
  )
})

export default AddDataModal;